import React, {
  forwardRef,
  lazy,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Circle,
  DirectionsRenderer,
  DirectionsService,
  DrawingManager,
  GoogleMap,
  InfoWindow,
  Marker,
  OverlayView,
  Polyline,
} from "@react-google-maps/api";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import moment from "moment-timezone";
import "./InfoWindow.css";
import "./MapJourney.css";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import * as THREE from "three";

const DynamicIconRender = lazy(() => import("../IconPicker/DynamicIconRender"));

const containerStyle = {
  width: "100%",
  height: "100%",
};

const MAP_ID = process.env.REACT_APP_GOOGLE_MAP_ID;

// Things about this component:
// 1. Renders markers on the map
// 2. Renders popUpContent when a marker is clicked
// 3. Centers the map when a marker is clicked
// 4. Renders icons on the map
// 5. Animates the map when the user navigates to different points
// 6. This is a controlled component. So whatever invokes this will need to set
// the default location / first item.
//7. Renders a 3d model if available and specified

const GoogleMapComponent = forwardRef(function GoogleMapComponent(props, ref) {
  // Event Types map is required if you want to render icons.
  const {
    activeMarker = null,
    // Additional map items are things like drawing tools, or other things that you want to render on the map
    additionalMapItems = null,

    circlesToRender = [],

    // This is used by routing, where is the user going
    destination,

    // Drawing Control allows a user to draw on the map
    drawingControl = false,

    // If facility markers are rendered on the map, this is the content that will be displayed when a facility marker is clicked.
    facilityPopUpContent = null,

    // This is the function that is called when a marker is clicked. It returns the marker object
    handleMarkerClick = null,

    highlightedZone = null,

    eventTypesMap = {},

    // We render facilities on the map. This is a list of facilities that are rendered on the map.
    facilities = [],
    floorPlanData = {},

    mapSettings = {
      center: { lat: 60.363007, lng: -43.6339 },
      mapTypeId: "satellite",
      zoom: 5,
    },
    markersToRender = [],

    // Marker key is what is used as a key when we iterate through the given markers
    markerKey = "assetHistoryId",

    // onPolygonComplete is a function that is called when a user is done drawing a polygon. It returns a array with a polygon object
    onPolygonComplete = () => { },

    onCircleClick = () => { },

    options = {},

    // This is used by routing, where is the user starting
    origin,

    // popUpContent is what appears when a marker is clicked.
    popUpContent = null,

    // popUpZoneContent is what appears when a zone (Circle) is clicked.
    popUpZoneContent = null,

    // Polylines show 'trips' devices take instead of a line of markers
    polylines = [],

    // Polygons
    polygons = [],

    // polylinePopUpContent is what appears when a polyline is clicked.
    polylinePopUpContent = null,
    setActiveMarker = () => { },
    setMapSettings = () => { },

    // This is used by routing, how is the user getting there
    travelMode = "WALKING",

    // This is used to control when the zone info window is open
    onCircleInfoWindowClose = () => { },

    // When true, draw dot-to-dot journey line between markers (in array order) with directional arrows
    showJourneyLine = false,

    // When true, show a small timestamp label next to each marker (no hover required)
    showTimestampsOnMarkers = false,

    // Timezone for formatting timestamps (e.g. "America/Chicago")
    timeZone = "UTC",

    // When true, the last marker in markersToRender is treated as "current" and rendered separately with emphasis (not clustered)
    emphasizeLatestMarker = false,

    // When true, show loading overlay (skeleton) over the map
    isLoading = false,

    // When no markers and this is set, show empty state message (e.g. "Select an asset to see journey")
    emptyStateMessage = null,

  } = props;

  const {
    center: rawCenter = { lat: 60.363007, lng: -43.6339 },
    mapTypeId = "satellite",
    zoom = 5,
  } = mapSettings ?? {};

  const defaultCenter = { lat: 60.363007, lng: -43.6339 };
  const center =
    rawCenter &&
    Number.isFinite(rawCenter.lat) &&
    Number.isFinite(rawCenter.lng)
      ? rawCenter
      : defaultCenter;

  const markers = Array.isArray(markersToRender) ? markersToRender : [];

  const mapRef = useRef();
  const overlayRef = useRef();
  const modelRef = useRef();
  const sceneRef = useRef();
  const cameraRef = useRef();
  const rendererRef = useRef();
  const [mapInstance, setMapInstance] = useState(null);

  // Track created markers to prevent duplicates
  const createdMarkersRef = useRef(new Map());

  const [isLoaded, setIsLoaded] = useState(true);
  const drawingManagerRef = useRef(null);
  const [state, setState] = useState({
    showInfoWindow: false,
    infoWindowContent: null,
    infoWindowLocation: {},
  });
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [requestDirections, setRequestDirections] = useState(false); // Control when to request directions
  const [isRouteRendered, setIsRouteRendered] = useState(false); // Track if the route has been rendered
  const [clusterer, setClusterer] = useState(null);

  // Dotted Line Path is used to show a line from the last navigable point to the destination
  const [dottedLinePath, setDottedLinePath] = useState([]); // State to store the dotted line path

  const { infoWindowContent, showInfoWindow, infoWindowLocation } = state;

  // Journey path: ordered positions for dot-to-dot connectivity when showing asset history
  const journeyPath = useMemo(() => {
    if (!showJourneyLine || markers.length < 2) return [];
    return markers.map((m) => ({
      lat: Number(m?.latitude),
      lng: Number(m?.longitude),
    })).filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  }, [showJourneyLine, markers]);

  // When emphasizing latest, split markers: all but last go to clusterer; last is "current" and rendered on top
  const { markersForCluster, latestMarker } = useMemo(() => {
    if (!emphasizeLatestMarker || markers.length === 0) {
      return { markersForCluster: markers, latestMarker: null };
    }
    if (markers.length === 1) {
      return { markersForCluster: [], latestMarker: markers[0] };
    }
    return {
      markersForCluster: markers.slice(0, -1),
      latestMarker: markers[markers.length - 1],
    };
  }, [emphasizeLatestMarker, markers]);

  // Format timestamp for map label (short, always visible)
  const formatMarkerTimestamp = useCallback((marker) => {
    const t = marker.timeOfLogLong ?? marker.timeOfLog;
    if (t == null) return null;
    const m = typeof t === "number" ? moment.utc(t).tz(timeZone) : moment.utc(t, "YYYYMMDDHHmmss").tz(timeZone);
    return m.isValid() ? m.format("MMM D, h:mm A") : null;
  }, [timeZone]);

  // Timestamp density: at low zoom or many markers, show only start, end, and current
  const shouldShowTimestampForMarker = useCallback((markerIndex, totalCount, isLatest) => {
    if (totalCount <= 8) return true;
    const currentZoom = mapSettings?.zoom ?? 5;
    if (currentZoom >= 14) return true;
    return markerIndex === 0 || markerIndex === totalCount - 1 || isLatest;
  }, [mapSettings?.zoom]);

  // Fit map to full journey bounds (with padding)
  const fitJourneyBounds = useCallback(() => {
    if (!mapRef.current || journeyPath.length < 2 || !window.google?.maps?.LatLngBounds) return;
    const bounds = new window.google.maps.LatLngBounds();
    journeyPath.forEach((p) => bounds.extend(p));
    mapRef.current.fitBounds(bounds, { top: 48, right: 48, bottom: 48, left: 48 });
  }, [journeyPath]);

  // Center and zoom on latest marker
  const centerOnLatest = useCallback(() => {
    if (!mapRef.current || !latestMarker) return;
    const lat = Number(latestMarker.latitude);
    const lng = Number(latestMarker.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    mapRef.current.panTo({ lat, lng });
    mapRef.current.setZoom(Math.max(mapRef.current.getZoom(), 15));
  }, [latestMarker]);

  const zoomIn = useCallback(() => {
    if (!mapRef.current) return;
    const z = mapRef.current.getZoom();
    const next = typeof z === "number" ? Math.min(z + 1, 21) : 15;
    mapRef.current.setZoom(next);
    setMapSettings((prev) => ({ ...(prev ?? {}), zoom: next }));
  }, [setMapSettings]);

  const zoomOut = useCallback(() => {
    if (!mapRef.current) return;
    const z = mapRef.current.getZoom();
    const next = typeof z === "number" ? Math.max(z - 1, 0) : 14;
    mapRef.current.setZoom(next);
    setMapSettings((prev) => ({ ...(prev ?? {}), zoom: next }));
  }, [setMapSettings]);

  // Pan map to show marker and popup (center marker, then shift view up so popup is in view)
  const adjustMapForPopup = useCallback((lat, lng) => {
    if (!mapRef.current || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const position = { lat: Number(lat), lng: Number(lng) };
    mapRef.current.panTo(position);
    const z = mapRef.current.getZoom();
    const newZoom = z < 15 ? 15 : z;
    if (z < 15) mapRef.current.setZoom(newZoom);
    setMapSettings((prev) => ({ ...prev, center: position, zoom: newZoom }));
    setTimeout(() => {
      if (mapRef.current) mapRef.current.panBy(0, -150);
    }, 400);
  }, [setMapSettings]);

  // Fit map bounds to all markers and facilities (for dashboard "Show all")
  const fitAllMarkers = useCallback(() => {
    if (!mapRef.current || !window.google?.maps?.LatLngBounds) return;
    const bounds = new window.google.maps.LatLngBounds();
    const extend = (lat, lng) => {
      if (Number.isFinite(lat) && Number.isFinite(lng)) bounds.extend({ lat: Number(lat), lng: Number(lng) });
    };
    markersToRender.forEach((m) => extend(m.latitude, m.longitude));
    (facilities || []).forEach((f) => {
      const loc = f.location || {};
      extend(loc.latitude, loc.longitude);
    });
    if (bounds.isEmpty()) return;
    mapRef.current.fitBounds(bounds, { top: 48, right: 48, bottom: 48, left: 48 });
  }, [markersToRender, facilities]);

  // Expose map actions to parent — all controls live in app header, nothing on map
  useImperativeHandle(ref, () => ({
    fitJourneyBounds,
    fitAllMarkers,
    centerOnLatest,
    zoomIn,
    zoomOut,
    hasJourneyPath: journeyPath.length >= 2,
    hasLatestMarker: !!latestMarker,
  }), [fitJourneyBounds, fitAllMarkers, centerOnLatest, zoomIn, zoomOut, journeyPath.length, latestMarker]);

  // Fit to journey only once when journey path is first available (do not override user zoom/pan)
  const hasFittedJourneyRef = useRef(false);
  useEffect(() => {
    if (!mapRef.current || journeyPath.length < 2 || !window.google?.maps?.LatLngBounds) return;
    if (hasFittedJourneyRef.current) return;
    hasFittedJourneyRef.current = true;
    const bounds = new window.google.maps.LatLngBounds();
    journeyPath.forEach((p) => bounds.extend(p));
    mapRef.current.fitBounds(bounds, { top: 48, right: 48, bottom: 48, left: 48 });
  }, [journeyPath]);
  useEffect(() => {
    if (journeyPath.length < 2) hasFittedJourneyRef.current = false;
  }, [journeyPath.length]);

  // We require two seperate info windows for markers and polylines.
  // The info window that sits on the marker is styled to sit directly ontop of the marker.
  // The UI is thrown off when we abstract this away from the marker component
  const [polylineInfoWindowState, setPolylineInfoWindowState] = useState({
    isOpen: false,
    position: null,
  });

  // Custom cluster renderer: green/navy branded markers (with fallback so it never throws)
  const clusterRenderer = useMemo(() => ({
    render(cluster, _stats, _map) {
      try {
        if (!window.google?.maps?.Marker || !cluster?.position) {
          return new window.google.maps.Marker({ position: cluster?.position ?? { lat: 0, lng: 0 } });
        }
        const count = cluster.count ?? cluster.size ?? 0;
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44"><circle cx="22" cy="22" r="20" fill="#A0C46D" stroke="#002843" stroke-width="2"/><text x="22" y="28" text-anchor="middle" fill="#fff" font-size="14" font-weight="700" font-family="system-ui,sans-serif">${count}</text></svg>`;
        const url = "data:image/svg+xml," + encodeURIComponent(svg);
        const Size = window.google.maps.Size;
        const Point = window.google.maps.Point;
        return new window.google.maps.Marker({
          position: cluster.position,
          icon: {
            url,
            scaledSize: Size ? new Size(44, 44) : undefined,
            anchor: Point ? new Point(22, 22) : undefined,
          },
          label: null,
          zIndex: 2,
        });
      } catch (_) {
        const pos = cluster?.position ?? { lat: 0, lng: 0 };
        return new window.google.maps.Marker({ position: pos, zIndex: 2 });
      }
    },
  }), []);

  // Update the onLoad callback to initialize the clusterer
  const onLoad = useCallback(function callback(map) {
    mapRef.current = map;
    setMapInstance(map);
    if (!window.google?.maps?.Marker) return;
    const options = {
      map,
      markers: [],
      algorithmOptions: { maxZoom: 18 },
      onClusterClick: (event, cluster, mapInstance) => {
        try {
          const bounds = cluster.bounds;
          if (bounds) {
            mapInstance.fitBounds(bounds, { top: 48, right: 48, bottom: 48, left: 48 });
          } else {
            mapInstance.setCenter(cluster.position);
            mapInstance.setZoom(Math.min(mapInstance.getZoom() + 2, 18));
          }
        } catch (e) {
          mapInstance.setCenter(cluster.position);
          mapInstance.setZoom(Math.min(mapInstance.getZoom() + 2, 18));
        }
      },
    };
    let markerClusterer;
    try {
      markerClusterer = new MarkerClusterer({ ...options, renderer: clusterRenderer });
    } catch (_) {
      markerClusterer = new MarkerClusterer(options);
    }
    setClusterer(markerClusterer);
  }, [clusterRenderer]);

  const onUnmount = useCallback(function callback() {
    mapRef.current = null;
  }, []);

  // Cleanup markers when markersToRender changes - remove old markers not in the new list
  useEffect(() => {
    if (!clusterer) return;

    // Get current marker IDs from markers
    const currentMarkerIds = new Set(markers.map(m => m?.[markerKey]));
    
    // Remove markers that are no longer in the list
    createdMarkersRef.current.forEach((googleMarker, id) => {
      if (!currentMarkerIds.has(id)) {
        googleMarker.setMap(null);
        clusterer.removeMarker(googleMarker);
        createdMarkersRef.current.delete(id);
      }
    });
  }, [markers, markerKey, clusterer]);

  // Cleanup all markers on component unmount
  useEffect(() => {
    return () => {
      if (createdMarkersRef.current) {
        createdMarkersRef.current.forEach((marker) => {
          marker.setMap(null);
        });
        createdMarkersRef.current.clear();
      }
    };
  }, []);

  const handlePolygonComplete = (polygon) => {
    const path = polygon
      .getPath()
      .getArray()
      .map((latLng) => ({
        lat: latLng.lat(),
        lng: latLng.lng(),
      }));
    const fillColor = "#008000";

    // setPolygonPaths([...polygonPaths, { path, fillColor }]);
    onPolygonComplete({ path, fillColor });
  };

  const handleDirectionsCallback = (response) => {
    if (response !== null && response.status === "OK") {
      setDirectionsResponse(response); // Save the response
      setRequestDirections(false); // Stop requesting directions after a successful response

      // Check if the destination is not reachable by road
      const route = response.routes[0];
      const lastLeg = route.legs[route.legs.length - 1];
      const lastPoint = lastLeg.steps[lastLeg.steps.length - 1].end_location;

      // Validate destination
      const destinationPoint =
        typeof destination === "string"
          ? {
            lat: parseFloat(destination.split(",")[0].trim()),
            lng: parseFloat(destination.split(",")[1].trim()),
          }
          : destination &&
            destination.lat !== undefined &&
            destination.lng !== undefined
            ? { lat: destination.lat, lng: destination.lng }
            : null;

      // If the last point is not the exact destination, add a dotted line
      if (
        destinationPoint &&
        (lastPoint.lat() !== destinationPoint.lat ||
          lastPoint.lng() !== destinationPoint.lng)
      ) {
        setDottedLinePath([
          { lat: lastPoint.lat(), lng: lastPoint.lng() }, // Last navigable point
          destinationPoint, // Exact destination
        ]);
      } else {
        setDottedLinePath([]); // Clear the dotted line if the destination is reachable
      }
    } else {
      setRequestDirections(false); // Stop requesting directions even if it fails
    }
  };

  // This useEffect is to animate the map as the user navigates to different points
  useEffect(() => {
    if (
      mapRef.current?.panTo &&
      mapSettings?.center &&
      Number.isFinite(mapSettings.center.lat) &&
      Number.isFinite(mapSettings.center.lng)
    ) {
      mapRef.current?.panTo(mapSettings.center);
    }
  }, [mapSettings?.center]);

  // Clear the route when destination is set to null
  useEffect(() => {
    if (!destination) {
      setDirectionsResponse(null); // Clear the directions response
      setIsRouteRendered(false); // Reset the route rendered state
    }
  }, [destination]);

  // Trigger DirectionsService only when origin or destination changes
  useEffect(() => {
    if (origin && destination) {
      setRequestDirections(true); // Enable DirectionsService rendering
    }
  }, [origin, destination]);

  // This useEffect listens to whether the destination or origin is set to null. If so, we clear the dottedLinePath
  useEffect(() => {
    if (!destination || !origin) {
      setDottedLinePath([]); // Reset the dotted line path to its initial state
    }
  }, [destination, origin]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || overlayRef.current) return;

    const overlay = new window.google.maps.WebGLOverlayView();
    overlayRef.current = overlay;

    overlay.onAdd = () => {
      const scene = new THREE.Scene();
      sceneRef.current = scene;

      const camera = new THREE.PerspectiveCamera();
      cameraRef.current = camera;

      const light = new THREE.AmbientLight(0xffffff, 0.75);
      scene.add(light);

      const dirLight = new THREE.DirectionalLight(0xffffff, 0.75);
      dirLight.position.set(0, 10, 0).normalize();
      scene.add(dirLight);

      const loader = new GLTFLoader();
      loader.load(floorPlanData.glbUrl ? floorPlanData?.glbUrl : "/house_floor_plan.glb", (gltf) => {
        const model = gltf.scene;

        model.scale.set(
          floorPlanData?.scale,
          floorPlanData?.scale,
          floorPlanData?.scale
        );

        model.rotateX(floorPlanData?.rotation?.x || 0); // Rotate around X-axis
        model.rotateY(floorPlanData?.rotation?.y || 0); // Rotate around Y-axis
        model.rotateZ(floorPlanData?.rotation?.z || 0); // Rotate around Z-axis

        modelRef.current = model;
        scene.add(model);
      });
    };

    overlay.onContextRestored = ({ gl }) => {
      const renderer = new THREE.WebGLRenderer({
        canvas: gl.canvas,
        context: gl,
        alpha: true,
      });
      renderer.autoClear = false;
      rendererRef.current = renderer;
    };

    overlay.onDraw = ({ gl, transformer }) => {
      const scene = sceneRef.current;
      const camera = cameraRef.current;
      const renderer = rendererRef.current;
      const model = modelRef.current;

      if (!scene || !camera || !renderer || !model) return;

      const matrix = transformer.fromLatLngAltitude({
        lat: floorPlanData?.center?.lat || center.lat,
        lng: floorPlanData?.center?.lng || center.lng,
        altitude: 0,
      });

      camera.projectionMatrix.fromArray(matrix);

      renderer.clearDepth();
      renderer.render(scene, camera);
      renderer.resetState();
    };

    overlay.setMap(mapRef.current);
  }, [isLoaded, center, floorPlanData]);

  useEffect(() => {
    if (!mapInstance) return;
    const container = mapInstance.getDiv();

    let startX, startY, startTilt, startHeading;

    const onMouseDown = (e) => {
      if (e.button !== 2) return; // right‐button only
      e.preventDefault();
      startX = e.clientX;
      startY = e.clientY;
      startTilt = mapInstance.getTilt() || 0;
      startHeading = mapInstance.getHeading() || 0;
      container.style.cursor = 'grabbing';
    };

    const onMouseMove = (e) => {
      if (!(e.buttons & 2)) {
        container.style.cursor = '';
        return;
      }

      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      const newHeading = startHeading + dx * 0.5;
      let newTilt = startTilt + dy * 0.5;
      newTilt = Math.max(0, Math.min(newTilt, mapInstance.maxTilt || 67.5));

      mapInstance.moveCamera({
        heading: newHeading,
        tilt: newTilt,
        center: mapInstance.getCenter(),
        zoom: mapInstance.getZoom(),
      });
    };

    const onMouseUp = (e) => {
      if (e.button !== 2) return;
      container.style.cursor = '';
    };

    const onContextMenu = (e) => {
      e.preventDefault();
    };

    container.addEventListener('mousedown', onMouseDown);
    container.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    container.addEventListener('contextmenu', onContextMenu);

    return () => {
      container.removeEventListener('mousedown', onMouseDown);
      container.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      container.removeEventListener('contextmenu', onContextMenu);
    };
  }, [mapInstance]);


  const showEmptyState = markers.length === 0 && emptyStateMessage;

  return isLoaded ? (
    <div
      className="map-journey-wrapper custom-css"
      style={{ position: "relative", width: "100%", height: "100%", minHeight: 200 }}
      role="application"
      aria-label={showJourneyLine ? "Asset journey map with path and locations" : "Asset location map"}
    >
      <div className="map-journey-map-container custom-css" style={{ position: "absolute", inset: 0, zIndex: 0 }}>
        <GoogleMap
        center={center}
        mapContainerStyle={containerStyle}
        onDragEnd={() => {
          if (!mapRef.current) return;
          const center = mapRef.current.getCenter().toJSON();
          const newZoom = mapRef.current.getZoom();

          setMapSettings((prevState) => ({
            ...(prevState ?? {}),
            center: { lat: center.lat, lng: center.lng },
            zoom: newZoom,
            mapTypeId: mapTypeId,
          }));
        }}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onMapTypeIdChanged={() => {
          const newMapTypeId = mapRef?.current?.getMapTypeId(); // Get the current mapTypeId

          // Only update the state if the mapTypeId has changed
          if (newMapTypeId !== mapSettings?.mapTypeId) {
            setMapSettings((prevState) => ({
              ...(prevState ?? {}),
              mapTypeId: newMapTypeId, // Update the mapTypeId in state
            }));
          }
        }}
        onZoomChanged={() => {
          const newZoom = mapRef?.current?.getZoom();

          setMapSettings((prevState) => ({
            ...(prevState ?? {}),
            zoom: newZoom != null ? newZoom : (prevState?.zoom ?? 5),
          }));

          // Close the info window if clustering is likely to occur
          if (newZoom < 16) {
            // Adjust the zoom level threshold as needed
            setActiveMarker(null); // Close the info window
          }
        }}
        options={{
          ...(options ?? {}),
          controlSize: 40, /* Google max; we scale to 44px in CSS for touch */
          draggable: true,
          fullscreenControl: true,
          gestureHandling: "greedy",
          mapId: MAP_ID,
          mapTypeControl: true,
          mapTypeControlOptions: {
            position: 1, // ControlPosition.LEFT_TOP
            style: 2,    // MapTypeControlStyle.DROPDOWN_MENU
          },
          mapTypeId: mapTypeId,
          rotateControl: true,
          scrollwheel: true,
          streetViewControl: true,
          zoomControl: true,
          zoomControlOptions: {
            position: 4, // ControlPosition.RIGHT_CENTER
          },
        }}
        yesIWantToUseGoogleMapApiInternals
        style={{
          height: "100%", // Ensure the map container has proper height
          width: "100%", // Ensure the map container has proper width
        }}
        zoom={zoom}
      >
        {/* General Info window */}
        {showInfoWindow && (
          <InfoWindow
            position={infoWindowLocation}
            onCloseClick={() =>
              setState({ showInfoWindow: false, infoWindowLocation: {} })
            }
          >
            {infoWindowContent}
          </InfoWindow>
        )}

        {/* Zones Pop-Up */}
        {highlightedZone && (() => {
          const zoneObject = circlesToRender.find(
            (circle) => circle.zoneId === highlightedZone
          );
          if (!zoneObject) return null;
          return (
            <InfoWindow
              position={{
                lat: zoneObject.xyz?.x || 0,
                lng: zoneObject.xyz?.y || 0,
              }}
              onCloseClick={() => onCircleInfoWindowClose()}
            >
              {popUpZoneContent ? popUpZoneContent(zoneObject) : null}
            </InfoWindow>
          );
        })()}

        {/* Info window for Polylines */}
        {polylineInfoWindowState.isOpen ? (
          <InfoWindow
            position={polylineInfoWindowState.position}
            onCloseClick={() =>
              setPolylineInfoWindowState({ isOpen: false, position: null })
            }
          >
            {polylinePopUpContent(polylineInfoWindowState)}
          </InfoWindow>
        ) : null}

        {/* Journey line: dot-to-dot with directional arrows */}
        {journeyPath.length >= 2 && (
          <Polyline
            path={journeyPath}
            options={{
              strokeColor: "#A0C46D",
              strokeOpacity: 0.95,
              strokeWeight: 3,
              geodesic: true,
              icons: [
                {
                  icon: {
                    path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                    scale: 3,
                    fillColor: "#002843",
                    fillOpacity: 1,
                    strokeColor: "#ffffff",
                    strokeWeight: 1,
                  },
                  offset: "100%",
                  repeat: "120px",
                },
              ],
            }}
          />
        )}

        {/* Start / End labels at high zoom */}
        {showJourneyLine && journeyPath.length >= 2 && (mapSettings?.zoom ?? 0) >= 14 && (
          <>
            <OverlayView position={journeyPath[0]} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
              <div className="map-journey-start-end" aria-hidden>Start</div>
            </OverlayView>
            <OverlayView position={journeyPath[journeyPath.length - 1]} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
              <div className="map-journey-start-end" aria-hidden>End</div>
            </OverlayView>
          </>
        )}

        {/* Markers */}
        {markers.length > 0 && clusterer &&
          markers.map((marker, markerIndex) => {
            const { event = "", latitude = 0, longitude = 0 } = marker;
            const uniqueIdentifierUUID = marker[markerKey];
            const isLatestMarker = emphasizeLatestMarker && latestMarker && marker[markerKey] === latestMarker[markerKey];
            const showTimestamp = showTimestampsOnMarkers && shouldShowTimestampForMarker(markerIndex, markers.length, isLatestMarker);
            const timestampLabel = showTimestamp ? formatMarkerTimestamp(marker) : null;

            if (eventTypesMap[event] && eventTypesMap[event].icon) {
              return (
                <React.Fragment key={uniqueIdentifierUUID}>
                  <OverlayView
                    position={{ lat: latitude, lng: longitude }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <div
                      onClick={() => {
                        setActiveMarker(uniqueIdentifierUUID);
                        adjustMapForPopup(latitude, longitude);
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setActiveMarker(uniqueIdentifierUUID);
                          adjustMapForPopup(latitude, longitude);
                        }
                      }}
                    >
                      <DynamicIconRender
                        iconName={eventTypesMap[event]?.icon || "LocationOn"}
                      />
                    </div>
                  </OverlayView>
                  {timestampLabel && (
                    <OverlayView
                      position={{ lat: latitude, lng: longitude }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <div className={`map-journey-timestamp ${isLatestMarker ? "map-journey-timestamp-current" : ""}`}>
                        {timestampLabel}
                      </div>
                    </OverlayView>
                  )}
                  {activeMarker === uniqueIdentifierUUID && popUpContent !== null && (
                    <InfoWindow
                      position={{ lat: latitude, lng: longitude }}
                      onCloseClick={() => setActiveMarker(null)}
                      options={window.google?.maps ? { pixelOffset: new window.google.maps.Size(0, 220), disableAutoPan: true } : undefined}
                    >
                      <div className="map-popup-premium">{popUpContent(marker, { onClose: () => setActiveMarker(null) })}</div>
                    </InfoWindow>
                  )}
                </React.Fragment>
              );
            }

            // Latest marker is rendered as a separate Marker below (emphasized); skip adding to clusterer
            if (!isLatestMarker) {
              if (!createdMarkersRef.current.has(uniqueIdentifierUUID)) {
                const googleMarker = new window.google.maps.Marker({
                  position: { lat: latitude, lng: longitude },
                  map: mapRef.current,
                  zIndex: 1,
                });
                googleMarker.addListener("click", () => {
                  if (handleMarkerClick) handleMarkerClick(marker);
                  else setActiveMarker(uniqueIdentifierUUID);
                  const pos = googleMarker.getPosition();
                  if (pos) adjustMapForPopup(pos.lat(), pos.lng());
                });
                createdMarkersRef.current.set(uniqueIdentifierUUID, googleMarker);
                clusterer.addMarker(googleMarker);
              }
            }

            return (
              <React.Fragment key={uniqueIdentifierUUID}>
                {timestampLabel && (
                  <OverlayView
                    position={{ lat: latitude, lng: longitude }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <div className={`map-journey-timestamp ${isLatestMarker ? "map-journey-timestamp-current" : ""}`}>
                      {timestampLabel}
                    </div>
                  </OverlayView>
                )}
                {activeMarker === uniqueIdentifierUUID && popUpContent !== null && (
                  <InfoWindow
                    position={{ lat: latitude, lng: longitude }}
                    onCloseClick={() => setActiveMarker(null)}
                    options={window.google?.maps ? { pixelOffset: new window.google.maps.Size(0, 220), disableAutoPan: true } : undefined}
                  >
                    <div className="map-popup-premium">{popUpContent(marker, { onClose: () => setActiveMarker(null) })}</div>
                  </InfoWindow>
                )}
              </React.Fragment>
            );
          })
        }

        {/* Current / latest location marker — always visible, never clustered */}
        {emphasizeLatestMarker && latestMarker && (() => {
          const { latitude = 0, longitude = 0 } = latestMarker;
          const uniqueId = latestMarker[markerKey];
          return (
            <Marker
              key={`current-${uniqueId}`}
              position={{ lat: latitude, lng: longitude }}
              zIndex={1000}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 10,
                fillColor: "#A0C46D",
                fillOpacity: 1,
                strokeColor: "#ffffff",
                strokeWeight: 3,
              }}
              onClick={() => {
                if (handleMarkerClick) handleMarkerClick(latestMarker);
                else setActiveMarker(uniqueId);
                adjustMapForPopup(latitude, longitude);
              }}
            >
              {activeMarker === uniqueId && popUpContent !== null && (
                <InfoWindow
                  position={{ lat: latitude, lng: longitude }}
                  onCloseClick={() => setActiveMarker(null)}
                  options={window.google?.maps ? { pixelOffset: new window.google.maps.Size(0, 220), disableAutoPan: true } : undefined}
                >
                  <div className="map-popup-premium">{popUpContent(latestMarker, { onClose: () => setActiveMarker(null) })}</div>
                </InfoWindow>
              )}
            </Marker>
          );
        })()}

        {/* Polylines (trips) — visually distinct from journey line when both shown */}
        {polylines.length > 0
          ? polylines.map((polyline, index) => {
            const { path = [], color, weight = 4 } = polyline;
            const isJourneyMode = showJourneyLine && journeyPath.length >= 2;
            const strokeColor = color || (isJourneyMode ? "#5B8DEE" : "blue");
            const strokeOpacity = isJourneyMode ? 0.85 : 1;
            const pathLatLng = Array.isArray(path) ? path.map((p) => ({ lat: p.lat, lng: p.lng })) : path;

            return (
              <Polyline
                key={index}
                onClick={(e) => {
                  setPolylineInfoWindowState({
                    data: polyline,
                    isOpen: true,
                    position: { lat: e.latLng.lat(), lng: e.latLng.lng() },
                  });
                }}
                options={{
                  strokeColor,
                  strokeWeight: weight,
                  strokeOpacity,
                  geodesic: true,
                }}
                path={pathLatLng}
              />
            );
          })
          : null}

        {/* Additional Map Items */}
        {additionalMapItems ? additionalMapItems : null}

        {/* Drawing Manager */}
        <DrawingManager
          ref={drawingManagerRef}
          options={{
            drawingControl,
            drawingControlOptions: {
              position: window.google.maps.ControlPosition.TOP_CENTER,
              drawingModes: [window.google.maps.drawing.OverlayType.POLYGON],
            },
            polygonOptions: {
              fillColor: "#ff0000",
              fillOpacity: 0.3,
              strokeOpacity: 1,
              strokeWeight: 5,
              visible: true,
            },
          }}
          // onPolygonComplete={(polygon) => {
          //   handlePolygonComplete(polygon);
          // }}
          onPolygonComplete={handlePolygonComplete}
        />


        {/* Polygons */}
        {/* {polygons.map((item, index) => {
          const {
            facility = {},
            path = [],
            fillColor = "#ff47d4",
            paths = [],
          } = item;
          const { location = {}, name = "" } = facility;
          const {
            address1 = "",
            city = "",
            state = "",
            postalCode = "",
          } = location;

          const polygonContentOnClick = <div>{name}</div>;

          return (
            <Polygon
              key={index}
              onClick={() =>
                setState((prevState) => ({
                  ...prevState,
                  infoWindowContent: PolygonPopUpContent({ facility }),
                  //conditionally render based on whether path or paths is provided
                  infoWindowLocation: path.length > 0 ? path[0] : paths[0],
                  showInfoWindow: true,
                }))
              }
              visible={true}
              options={{ strokeColor: "purple", fillColor, fillOpacity: 0.1 }}
              paths={path.length > 0 ? path : paths} // Use path or paths based on availability
            />
          );
        })} */}

        {/* Zones */}
        {/* use data from  circlesToRender. has center.lat, center.long, and radius as well as name which i want rendered in popup*/}
        {circlesToRender.map((circle, index) => {
          const { count = 0, xyz = {}, radius = 100, name = "", zoneId } = circle;
          const { x = 0, y = 0 } = xyz;

          return (
            <React.Fragment key={`zone-wrapper-${index}`}>
              <Circle
                center={{ lat: x, lng: y }}
                radius={radius}
                options={{
                  strokeColor: "green",
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                  fillColor: highlightedZone === zoneId ? "red" : "green",
                  fillOpacity: 0.35,
                }}
                onClick={() => {
                  onCircleClick(zoneId); // <-- pass zoneId back
                }}
              />
              <OverlayView
                position={{ lat: x, lng: y }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "white",
                    fontSize: "14px",
                    fontWeight: "bold",
                    textShadow: "1px 1px 2px black",
                    pointerEvents: "none", // Make the div click-through
                  }}
                >
                  {count}
                </div>
              </OverlayView>
            </React.Fragment>
          );
        })}

        {/* Facilities Markers */}
        {facilities.map((facility, index) => {
          const { facilityId = "", location = {} } = facility;
          const { latitude = 0, longitude = 0 } = location;

          return (
            <Marker
              key={`facility-${index}`}
              position={{ lat: latitude, lng: longitude }}
              icon={{
                url:
                  "data:image/svg+xml;charset=UTF-8," +
                  encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="blue" viewBox="0 0 24 24">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
        `),
                scaledSize: new window.google.maps.Size(24, 24), // Adjust size as needed
              }}
              onClick={() => {
                setActiveMarker(facilityId);
                adjustMapForPopup(latitude, longitude);
              }}
              zIndex={1000} // Ensure the marker is on top of other elements
            >
              {/* Clicking on a marker sets the active marker as the unique identifier. This lets us know which marker to display */}
              {activeMarker === facilityId && facilityPopUpContent != null ? (
                <InfoWindow
                  onCloseClick={() => setActiveMarker(null)}
                  options={window.google?.maps ? { disableAutoPan: true } : undefined}
                >
                  {facilityPopUpContent(facility, { onClose: () => setActiveMarker(null) })}
                </InfoWindow>
              ) : null}
            </Marker>
          );
        })}

        {/* Directions Service */}
        {requestDirections && origin && destination && (
          <DirectionsService
            options={{
              origin,
              destination,
              travelMode,
            }}
            callback={handleDirectionsCallback}
          />
        )}

        {/* Directions Renderer */}
        {directionsResponse && (
          <DirectionsRenderer
            options={{
              directions: directionsResponse,
              preserveViewport: isRouteRendered, // Preserve viewport after the initial render
              draggable: true, // Allow the user to drag the route
            }}
            onLoad={(directionsRenderer) => {
              // Ensure the DirectionsRenderer does not block map interactions
              directionsRenderer.setOptions({
                draggable: true,
                preserveViewport: isRouteRendered,
              });

              // Mark the route as rendered after the initial load
              if (!isRouteRendered) {
                setIsRouteRendered(true);
              }

              // Fit the map to the route bounds
              const bounds =
                directionsRenderer.getDirections().routes[0].bounds;
              mapRef.current.fitBounds(bounds);
            }}
          />
        )}

        {/* Dotted Line for Non-Navigable Path */}
        {dottedLinePath.length > 0 && (
          <Polyline
            path={dottedLinePath}
            options={{
              strokeColor: "#0000FF", // color for the dotted line
              strokeOpacity: 0, // Make the main line fully transparent
              strokeWeight: 2, // Thickness of the line
              icons: [
                {
                  icon: {
                    path: "M 0,-1 0,1", // Dotted pattern
                    strokeOpacity: 0.5, // Opacity of the dots
                    scale: 2, // Size of the dots
                  },
                  offset: "0", // Start position of the pattern
                  repeat: "10px", // Distance between dots
                },
              ],
            }}
          />
        )}
      </GoogleMap>
      </div>

      {/* Empty state — premium: icon + message */}
      {showEmptyState && (
        <div className="map-empty-state" role="status">
          <div className="map-empty-state-icon" aria-hidden>
            <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
            </svg>
          </div>
          <p className="map-empty-state-message">{emptyStateMessage}</p>
        </div>
      )}

      {/* Loading overlay */}
      {isLoading && (
        <div className="map-loading-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="map-loading-skeleton" />
          <span className="map-loading-text">Loading map…</span>
        </div>
      )}
    </div>
  ) : null;
});

export default GoogleMapComponent;
